import { useCallback, useEffect, useState } from "react";
import { toast } from "sonner";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { api, errMsg } from "@/lib/api";
import { fmtDateTime, formatINR, safeHref } from "@/lib/format";
import { PageHead, Pill, In, Sel, KV, Panel } from "@/components/admin/kit";
import { ActivityList, NotificationList, NotesList } from "@/components/admin/Lists";
import { NoteForm } from "@/components/admin/NoteForm";
import { Loading, Empty } from "@/components/site/Primitives";

const L = ({ url }) => (url ? <a href={safeHref(url)} target="_blank" rel="noopener noreferrer" className="text-[#00E5FF] underline break-all">{url}</a> : null);

const Detail = ({ id, onClose, onChanged }) => {
  const [d, setD] = useState(null);
  const load = useCallback(() => api.get(`/admin/applications/${id}`).then((r) => setD(r.data)), [id]);
  useEffect(() => {
    load();
  }, [load]);
  const patch = async (body, msg) => {
    try { await api.patch(`/admin/applications/${id}`, body); toast.success(msg); load(); onChanged(); return true; } catch (e) { toast.error(errMsg(e)); return false; }
  };
  const a = d?.application;
  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-4xl bg-[#0a0a0a] border-[#27272A] max-h-[90vh] overflow-auto rounded-none" data-testid="application-detail">
        {!a ? <Loading rows={1} /> : <>
          <DialogHeader><DialogTitle className="font-display text-2xl"><span className="font-mono text-[#00E5FF] mr-3">{a.reference}</span>{a.name}</DialogTitle></DialogHeader>
          <div className="grid md:grid-cols-5 gap-6">
            <dl className="md:col-span-3">
              <KV k="Type" v={a.type === "editor" ? "Editor application" : a.application_type} />
              <KV k="Contact" v={`${a.email}${a.phone ? ` · ${a.phone}` : ""}`} />
              <KV k="Location" v={a.location} />
              {a.type === "editor" ? <>
                <KV k="Showreel" v={<L url={a.showreel_url} />} />
                <KV k="Portfolio" v={(a.portfolio_links || []).length ? <div className="space-y-1">{a.portfolio_links.map((u) => <L key={u} url={u} />)}</div> : null} />
                <KV k="Experience" v={`${a.experience_years} years`} />
                <KV k="Skills" v={a.skills?.join(", ")} />
                <KV k="Software" v={a.software?.join(", ")} />
                <KV k="Rate / reel" v={a.rate_per_reel_inr != null ? formatINR(a.rate_per_reel_inr) : null} />
                <KV k="Rate notes" v={a.rate_notes} />
                <KV k="Availability" v={`${a.availability}${a.hours_per_week ? ` · ${a.hours_per_week} h/week` : ""}${a.start_date ? ` · from ${a.start_date}` : ""}`} />
                <KV k="About" v={a.about} />
              </> : <>
                <KV k="Company" v={a.company} />
                <KV k="Website" v={<L url={a.company_website} />} />
                <KV k="Volume" v={a.monthly_volume} />
                <KV k="Platforms" v={a.platforms?.join(", ")} />
                <KV k="Details" v={a.details} />
                <KV k="Sample" v={<L url={a.sample_links} />} />
                <KV k="Budget" v={a.budget_range} />
              </>}
            </dl>
            <div className="md:col-span-2 space-y-4">
              <Sel value={a.status} onChange={(v) => patch({ status: v }, "Status updated")} options={d.statuses} testid="application-status-select" />
              <Panel title="Private notes"><NotesList notes={a.notes} /><NoteForm testid="application-note" onSubmit={(t) => patch({ note: t }, "Note added")} /></Panel>
              <Panel title="Emails"><NotificationList items={d.notifications} /></Panel>
              <Panel title="Activity"><ActivityList items={d.activity} /></Panel>
            </div>
          </div>
        </>}
      </DialogContent>
    </Dialog>
  );
};

export default function ApplicationsAdmin() {
  const [items, setItems] = useState(null);
  const [type, setType] = useState("");
  const [status, setStatus] = useState("");
  const [q, setQ] = useState("");
  const [search, setSearch] = useState("");
  const [open, setOpen] = useState(null);
  const load = useCallback(() => api.get("/admin/applications", { params: { type, status, q: search } }).then((r) => setItems(r.data)), [type, status, search]);
  useEffect(() => {
    load();
  }, [load]);
  return (
    <div data-testid="admin-applications">
      <PageHead eyebrow="Operations" title="Applications" />
      <div className="grid md:grid-cols-4 gap-3 mb-6">
        <form className="md:col-span-2" onSubmit={(e) => { e.preventDefault(); setSearch(q); }}><In value={q} onChange={setQ} placeholder="Search name, email, company, ref" testid="applications-search" /></form>
        <Sel value={type} onChange={setType} options={[{ value: "editor", label: "Editors" }, { value: "partner", label: "Partners" }]} placeholder="All types" testid="applications-filter-type" />
        <Sel value={status} onChange={setStatus} options={["New", "Reviewing", "Shortlisted", "Trial Task", "In Discussion", "Accepted", "Rejected", "Declined"]} placeholder="All statuses" testid="applications-filter-status" />
      </div>
      {!items ? <Loading rows={1} /> : items.length === 0 ? <Empty title="No applications yet." body="Submissions from the public /apply page appear here." /> : (
        <div className="overflow-x-auto border border-[#27272A]">
          <table className="w-full text-sm" data-testid="applications-table">
            <thead><tr className="text-left text-xs uppercase tracking-wider text-zinc-500 border-b border-[#27272A]">{["Reference", "Applicant", "Type", "Details", "Received", "Status"].map((h) => <th key={h} className="px-4 py-3 font-medium">{h}</th>)}</tr></thead>
            <tbody>
              {items.map((a) => (
                <tr key={a.id} onClick={() => setOpen(a.id)} className="border-b border-[#27272A] last:border-0 hover:bg-[#121212] cursor-pointer" data-testid={`application-row-${a.reference}`}>
                  <td className="px-4 py-3 font-mono text-[#00E5FF] whitespace-nowrap">{a.reference}</td>
                  <td className="px-4 py-3"><p>{a.name}</p><p className="text-xs text-zinc-500">{a.email}</p></td>
                  <td className="px-4 py-3 whitespace-nowrap">{a.type === "editor" ? "Editor" : a.application_type}</td>
                  <td className="px-4 py-3 text-zinc-400 max-w-[280px] truncate">{a.type === "editor" ? `${a.experience_years} yrs · ${a.skills?.slice(0, 3).join(", ")}` : a.company}</td>
                  <td className="px-4 py-3 text-zinc-400 whitespace-nowrap">{fmtDateTime(a.created_at)}</td>
                  <td className="px-4 py-3"><Pill status={a.status} /></td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
      {open && <Detail id={open} onClose={() => setOpen(null)} onChanged={load} />}
    </div>
  );
}
